import { useMutation } from '@tanstack/react-query';
import { notification } from 'antd';
import { FormikValues } from 'formik';

import api from '../../../utils/api';
import { MatricFormatSegmentPayload } from './useCreateMatricFormatMutation';

export interface PreviewMatricFormatPayload {
  segments: MatricFormatSegmentPayload[];
  sessionId: number;
  facultyId: number;
  programId: number;
  programTypeId: number;
}

export interface PreviewMatricFormatResponse {
  statusCode: number;
  message: string;
  data: string;
}

const toPreviewPayload = (values: FormikValues | PreviewMatricFormatPayload): PreviewMatricFormatPayload => ({
  segments: (values?.segments ?? []).map((segment: FormikValues) => ({
    order: Number(segment?.order ?? 0),
    segmentType:
      typeof segment?.segmentType === 'number' ? segment.segmentType : Number(segment?.segmentType ?? 0),
    staticValue: segment?.staticValue,
    padding:
      segment?.padding === null || typeof segment?.padding === 'undefined' || segment?.padding === ''
        ? undefined
        : Number(segment.padding),
    yearFormat: segment?.yearFormat,
  })),
  sessionId: Number(values?.sessionId ?? 0),
  facultyId: Number(values?.facultyId ?? 0),
  programId: Number(values?.programId ?? 0),
  programTypeId: Number(values?.programTypeId ?? 0),
});

export const usePreviewMatricFormatMutation = () => {
  const previewMatricFormat = async (payload: PreviewMatricFormatPayload): Promise<PreviewMatricFormatResponse> => {
    return (await api.post('/MatricFormat/preview', payload))?.data;
  };

  const mutation = useMutation({
    mutationKey: ['preview-matric-format'],
    mutationFn: previewMatricFormat,
  });

  const previewMatricFormatHandler = async (values: FormikValues | PreviewMatricFormatPayload) => {
    try {
      return await mutation.mutateAsync(toPreviewPayload(values));
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description: error?.response?.data?.message || error?.response?.data?.title || 'Failed to preview matric format',
      });

      return undefined;
    }
  };

  return {
    previewMatricFormatHandler,
    previewMatricFormat: mutation.mutateAsync,
    previewData: mutation.data?.data,
    data: mutation.data,
    isPreviewLoading: mutation.isPending,
    isLoading: mutation.isPending,
  };
};